/******************************************
*  Author : Riccardo Di Dato
*  Created On : Fri May 27 2022
*  File : GoogleProductsInitializerConfig.ts
*******************************************/
import { isObject } from "../TypeChecking/index.js";
import { hasOwnProperty } from "../Utils/index.js";
/**
 * Rappresenta il blocco trackingOptions di Google Analytics completato utilizzando i valori di default quando mancanti
 */
export class AnalyticsTrackingOptions {
    static DEFAULT_IN_APP_TRACKING = true;
    static DEFAULT_SAME_SITE = false;
    static DEFAULT_USE_COOKIE_DOMAIN = 'auto';
    static DEFAULT_AUTOLINK_HOSTS = [];
    /**
     * Default {@link DEFAULT_IN_APP_TRACKING}
     */
    inAppTracking;
    /**
     * Default {@link DEFAULT_SAME_SITE}
     */
    sameSite;
    /**
     * Default {@link DEFAULT_USE_COOKIE_DOMAIN}
     */
    useCookieDomain;
    /**
     * Default {@link DEFAULT_AUTOLINK_HOSTS}
     */
    autolinkHosts;
    constructor(partial) {
        this.inAppTracking = hasOwnProperty(partial, "inAppTracking") ? partial.inAppTracking : AnalyticsTrackingOptions.DEFAULT_IN_APP_TRACKING;
        this.sameSite = hasOwnProperty(partial, "sameSite") ? partial.sameSite : AnalyticsTrackingOptions.DEFAULT_SAME_SITE;
        this.useCookieDomain = hasOwnProperty(partial, "useCookieDomain") ? partial.useCookieDomain : AnalyticsTrackingOptions.DEFAULT_USE_COOKIE_DOMAIN;
        this.autolinkHosts = hasOwnProperty(partial, "autolinkHosts") ? partial.autolinkHosts : AnalyticsTrackingOptions.DEFAULT_AUTOLINK_HOSTS;
    }
}
/**
 * Rappresenta la configurazione completa di Google Analytics applicando valori di default quando mancanti
 */
export class AnalyticsConfig {
    static DEFAULT_LAZY_INITIALIZE = false;
    static DEFAULT_PRELOAD = true;
    static DEFAULT_SUBNAME = "";
    static DEFAULT_CALLBACK_TIMEOUT = 500;
    /**
     * Id account
     */
    accountId;
    /**
      * Determina se inizializzare il prima possibile (false) o alla prima richiesta dell'inizializer (true).
      * Defaiult {@link DEFAULT_LAZY_INITIALIZE}
      */
    lazyInitialize;
    /**
      * Determina se effettuare il preload della libreria. Ignorato se {@link lazyInitialize} è false.
      * Default {@link DEFAULT_PRELOAD}
      */
    preload;
    defer;
    /**
      * Determina il prefisso da utilizzare per le chiamate ad analytics.
      * Default {@link DEFAULT_SUBNAME}
      */
    subname;
    trackingOptions;
    applyUserId;
    /**
     * Tempo di timeout per le richieste in millisecond.
     * Default {@link DEFAULT_CALLBACK_TIMEOUT}
     */
    callbackTimeout;
    constructor(partial) {
        this.accountId = partial.accountId;
        this.lazyInitialize = hasOwnProperty(partial, "lazyInitialize") ? partial.lazyInitialize : AnalyticsConfig.DEFAULT_LAZY_INITIALIZE;
        this.preload = hasOwnProperty(partial, "preload") ? partial.preload : AnalyticsConfig.DEFAULT_PRELOAD;
        this.subname = hasOwnProperty(partial, "subname") ? partial.subname : AnalyticsConfig.DEFAULT_SUBNAME;
        this.defer = hasOwnProperty(partial, "defer") ? partial.defer : false;
        this.applyUserId = hasOwnProperty(partial, "applyUserId") ? partial.applyUserId : false;
        this.trackingOptions = new AnalyticsTrackingOptions(hasOwnProperty(partial, "trackingOptions") ?
            partial.trackingOptions :
            {});
        this.callbackTimeout = hasOwnProperty(partial, "callbackTimeout") ? partial.callbackTimeout : AnalyticsConfig.DEFAULT_CALLBACK_TIMEOUT;
    }
}
/**
 * Rappresenta la configurazione completa di Google Optimize applicando valori di default quando mancanti
 * @deprecated Optimize è stato dismesso
 */
export class OptimizeConfig {
    static DEFAULT_LAZY_INITIALIZE = false;
    static DEFAULT_PRELOAD = true;
    /**
     * Id account
     */
    accountId;
    /**
      * Determina se inizializzare il prima possibile (false) o alla prima richiesta dell'inizializer (true).
      * Default {@link DEFAULT_LAZY_INITIALIZE}
      */
    lazyInitialize;
    /**
      * Determina se effettuare il preload della libreria. Ignorato se {@link lazyInitialize} è false.
      * Default {@link DEFAULT_PRELOAD}
      */
    preload;
    constructor(partial) {
        this.accountId = partial.accountId;
        this.lazyInitialize = hasOwnProperty(partial, "lazyInitialize") ? partial.lazyInitialize : OptimizeConfig.DEFAULT_LAZY_INITIALIZE;
        this.preload = hasOwnProperty(partial, "preload") ? partial.preload : OptimizeConfig.DEFAULT_PRELOAD;
    }
}
export class GA4Config extends AnalyticsConfig {
}
export function isGoogleMinimal(obj) {
    return isObject(obj) && hasOwnProperty(obj, "analytics");
}
/**
 * @deprecated Optimize è stato dismesso
 * @param obj
 * @returns
 */
export function isGoogleWithOptimize(obj) {
    return isGoogleMinimal(obj) && hasOwnProperty(obj, "ga4") && hasOwnProperty(obj, "optimize");
}
export function isGoogleProductsInitializerConfigPartial(obj) {
    return isGoogleMinimal(obj) && isGoogleWithOptimize(obj);
}
/**
 * Aggregatore delle configurazioni complete di Google Analytics ed Optimize.
 */
export class GoogleProductsInitializerConfig {
    analytics;
    /**
     * @deprecated Optimize è stato dismesso
     */
    optimize = null;
    ga4 = null;
    constructor(partial) {
        this.analytics = new AnalyticsConfig(partial.analytics);
        if (partial.ga4) {
            this.ga4 = new GA4Config(partial.ga4);
        }
        if (isGoogleWithOptimize(partial)) {
            this.optimize = new OptimizeConfig(partial.optimize);
        }
    }
}
